import React, { useState, useEffect, useContext } from 'react';
import api, { clearStoredSession, getUserFacingErrorMessage, isAuthError } from '../utils/api';
import { UserContext } from '../App';
import { useNavigate, useLocation } from 'react-router-dom';
import ContextSwitcher from './ContextSwitcher';
import NomineeRequiredBanner from './NomineeRequiredBanner';
import OwnerShell from './OwnerShell';
import ResponsiveOptionNav from './ResponsiveOptionNav';
import { Button, Modal } from '../ui';

const OTHER_CATEGORIES = [
  'Final Wishes',
  'Personal Notes',
  'Physical Assets',
  'Digital Assets'
];

const TYPE_OPTIONS = {
  'Final Wishes': ['Funeral Preferences', 'Burial Instructions', 'Memorial Service', 'Organ Donation'],
  'Personal Notes': ['Message to Loved Ones', 'Life Story', 'Advice', 'Memories'],
  'Physical Assets': ['Safety Deposit Box', 'Home Safe', 'Storage Unit', 'Other Location'],
  'Digital Assets': ['Social Media', 'Email Accounts', 'Cloud Storage', 'Cryptocurrency']
};

export default function OtherAssets() {
  const { currentUser, users, setCurrentUser } = useContext(UserContext);
  const [assets, setAssets] = useState([]);
  const [hasNominees, setHasNominees] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [attachmentsOpen, setAttachmentsOpen] = useState(false);
  const [attachmentsAsset, setAttachmentsAsset] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [attachmentsBusy, setAttachmentsBusy] = useState(false);
  const [attachmentsError, setAttachmentsError] = useState('');
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [attachmentUploadBusy, setAttachmentUploadBusy] = useState(false);
  const [attachmentUploadProgress, setAttachmentUploadProgress] = useState(null); // { pct, label }
  const [editingAssetId, setEditingAssetId] = useState(null);
  const [form, setForm] = useState({ type: '', typeCategory: '', institution: '', accountNo: '', value: '', notes: '' });
  const [activeCategory, setActiveCategory] = useState('Final Wishes');
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [step, setStep] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  // Asset owner is the current logged-in user
  const assetHolder = currentUser;
  if (!assetHolder) {
    return <div>Loading...</div>;
  }

  // Nominee journey now lives under /nominee.
  useEffect(() => {
    if (currentUser?.type === 'nominee') {
      navigate('/nominee');
    }
  }, [currentUser?.type, navigate]);

  if (currentUser?.type === 'nominee') {
    return <div style={{ padding: 20 }}>Redirecting…</div>;
  }

  // Fetch nominee presence (owner-scoped) to decide whether to allow asset mutations.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/nominees');
        if (cancelled) return;
        const list = Array.isArray(res.data) ? res.data : [];
        setHasNominees(list.length > 0);
      } catch (err) {
        if (cancelled) return;
        console.error('OtherAssets: failed to load nominees', err?.response?.data || err);
        if (isAuthError(err)) {
          clearStoredSession();
          setCurrentUser(null);
        } else {
          setHasNominees(null);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [setCurrentUser]);

  // Fetch other assets for asset holder (owner view)
  useEffect(() => {
    if (currentUser.type === 'owner') {
      let cancelled = false;
      (async () => {
        try {
          const res = await api.get('/assets');
          if (cancelled) return;
          const all = Array.isArray(res.data) ? res.data : [];
          const holderAssets = all.filter(a => a.ownerId === assetHolder.id && OTHER_CATEGORIES.includes(a.typeCategory));
          setAssets(holderAssets);
          if (holderAssets.length > 0) {
            const firstCat = OTHER_CATEGORIES.find(cat => holderAssets.some(a => a.typeCategory === cat));
            setActiveCategory(firstCat || OTHER_CATEGORIES[0]);
          } else {
            setActiveCategory(OTHER_CATEGORIES[0]);
          }
        } catch (err) {
          if (cancelled) return;
          console.error('OtherAssets: failed to load assets', err?.response?.data || err);
          if (isAuthError(err)) {
            clearStoredSession();
            setCurrentUser(null);
          }
        }
      })();
      return () => { cancelled = true; };
    }
  }, [currentUser, assetHolder.id]);

  // Note: nominee views were removed from this screen in favour of /nominee/* pages.

  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });

  // Step logic
  const resetForm = () => {
    setForm({ type: '', typeCategory: '', institution: '', accountNo: '', value: '', notes: '' });
    setStep(0);
    setEditingAssetId(null);
  };

  const refreshAssets = async () => {
    const res = await api.get('/assets');
    setAssets(res.data.filter(a => a.ownerId === assetHolder.id && OTHER_CATEGORIES.includes(a.typeCategory)));
  };

  const guessContentType = (f) => {
    const name = String(f?.name || '');
    const ext = (name.split('.').pop() || '').toLowerCase();
    if (f?.type) return f.type;
    // Fallbacks for browsers that don't populate File.type reliably.
    if (ext === 'pdf') return 'application/pdf';
    if (ext === 'docx') return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    if (ext === 'png') return 'image/png';
    if (ext === 'jpg' || ext === 'jpeg') return 'image/jpeg';
    if (ext === 'mp3') return 'audio/mpeg';
    if (ext === 'wav') return 'audio/wav';
    if (ext === 'm4a') return 'audio/mp4';
    if (ext === 'mp4') return 'video/mp4';
    if (ext === 'mov') return 'video/quicktime';
    if (ext === 'webm') return 'video/webm';
    return 'application/octet-stream';
  };

  const loadAttachments = async (assetId) => {
    const resp = await api.get(`/assets/${encodeURIComponent(assetId)}/attachments`);
    setAttachments(Array.isArray(resp.data) ? resp.data : []);
  };

  const openAttachmentsModal = async (asset) => {
    if (!asset?.id) return;
    setAttachmentsAsset(asset);
    setAttachmentsOpen(true);
    setAttachmentsError('');
    setAttachmentFile(null);
    setAttachmentUploadProgress(null);
    try {
      setAttachmentsBusy(true);
      await loadAttachments(asset.id);
    } catch (e) {
      console.error('Failed to load attachments', e?.response?.data || e);
      setAttachmentsError(getUserFacingErrorMessage(e, {
        fallback: 'We could not load attachments. Please try again.',
        includeSupportForUnknown: false,
      }));
    } finally {
      setAttachmentsBusy(false);
    }
  };

  const downloadAttachment = async ({ assetId, objectId }) => {
    const r = await api.get(`/assets/${encodeURIComponent(assetId)}/attachments/${encodeURIComponent(objectId)}/download`);
    const url = r?.data?.url;
    if (!url) throw new Error('No URL returned');
    window.location.assign(url);
  };

  const uploadToS3PresignedPost = async ({ upload, file }) => {
    const formData = new FormData();
    Object.entries(upload.fields || {}).forEach(([k, v]) => formData.append(k, v));
    formData.append('file', file);
    const resp = await fetch(upload.url, { method: 'POST', body: formData });
    if (!resp.ok) throw new Error(`S3 upload failed (HTTP ${resp.status})`);
  };

  const uploadAttachmentSingle = async ({ assetId, file }) => {
    const contentType = guessContentType(file);
    const init = await api.post(`/assets/${encodeURIComponent(assetId)}/attachments/upload/init`, {
      fileName: file.name,
      contentType,
      bytesExpected: file.size,
    });
    const { objectId, upload } = init.data || {};
    if (!objectId || !upload?.url || !upload?.fields) throw new Error('Upload init failed');
    await uploadToS3PresignedPost({ upload, file });
    await api.post(`/assets/${encodeURIComponent(assetId)}/attachments/upload/finalize`, { objectId });
  };

  const uploadAttachmentMultipart = async ({ assetId, file }) => {
    const contentType = guessContentType(file);
    const init = await api.post(`/assets/${encodeURIComponent(assetId)}/attachments/multipart/init`, {
      fileName: file.name,
      contentType,
      totalBytes: file.size,
    });
    const { objectId, uploadId, partSizeBytes } = init.data || {};
    if (!objectId || !uploadId || !partSizeBytes) throw new Error('Multipart init failed');

    const total = file.size;
    const partSize = Number(partSizeBytes);
    const partCount = Math.ceil(total / partSize);
    const parts = [];

    let uploaded = 0;
    for (let partNumber = 1; partNumber <= partCount; partNumber++) {
      const start = (partNumber - 1) * partSize;
      const end = Math.min(start + partSize, total);
      const chunk = file.slice(start, end);

      setAttachmentUploadProgress({
        pct: Math.round((uploaded / total) * 100),
        label: `Uploading part ${partNumber}/${partCount}…`,
      });

      const pres = await api.post(`/assets/${encodeURIComponent(assetId)}/attachments/multipart/part-url`, {
        objectId,
        uploadId,
        partNumber,
      });
      const url = pres?.data?.url;
      if (!url) throw new Error('Failed to presign part URL');

      const resp = await fetch(url, { method: 'PUT', body: chunk });
      if (!resp.ok) throw new Error(`Part upload failed (HTTP ${resp.status})`);
      const etag = resp.headers.get('etag');
      if (!etag) throw new Error('Missing ETag from S3 part upload');
      parts.push({ partNumber, etag });
      uploaded += (end - start);
    }

    setAttachmentUploadProgress({ pct: 100, label: 'Finalizing upload…' });
    await api.post(`/assets/${encodeURIComponent(assetId)}/attachments/multipart/complete`, {
      objectId,
      uploadId,
      parts,
    });
  };

  const uploadAttachment = async () => {
    if (!attachmentsAsset?.id) return;
    if (!attachmentFile) return;
    if (hasNominees === false) {
      navigate('/account?tab=nominees', { state: { tab: 'nominees', returnTo: location.pathname } });
      return;
    }
    setAttachmentUploadBusy(true);
    setAttachmentsError('');
    try {
      const ct = guessContentType(attachmentFile);
      const isVideo = /^video\\//i.test(ct);
      const useMultipart = isVideo && attachmentFile.size > 250 * 1024 * 1024;
      if (useMultipart) {
        await uploadAttachmentMultipart({ assetId: attachmentsAsset.id, file: attachmentFile });
      } else {
        await uploadAttachmentSingle({ assetId: attachmentsAsset.id, file: attachmentFile });
      }
      setAttachmentFile(null);
      setAttachmentUploadProgress(null);
      await loadAttachments(attachmentsAsset.id);
      alert('Attachment uploaded.');
    } catch (e) {
      console.error('Attachment upload failed', e?.response?.data || e);
      const code = e?.response?.data?.code;
      if (e?.response?.status === 409 && code === 'NOMINEE_REQUIRED') {
        alert(getUserFacingErrorMessage(e, {
          fallback: 'Add at least one nominee before you can upload attachments.',
          includeSupportForUnknown: false,
        }));
        navigate('/account?tab=nominees', { state: { tab: 'nominees', returnTo: location.pathname } });
        return;
      }
      setAttachmentsError(getUserFacingErrorMessage(e, {
        fallback: 'We could not upload that attachment. Please try again.',
        includeSupportForUnknown: false,
      }));
    } finally {
      setAttachmentUploadBusy(false);
    }
  };

  const openModal = (assetToEdit = null) => {
    if (hasNominees === false) {
      navigate('/account?tab=nominees', { state: { tab: 'nominees', returnTo: location.pathname } });
      return;
    }
    setModalOpen(true);
    if (assetToEdit) {
      setEditingAssetId(assetToEdit.id);
      setForm({
        type: assetToEdit.type || '',
        typeCategory: assetToEdit.typeCategory || '',
        institution: assetToEdit.institution || '',
        accountNo: assetToEdit.accountNo || '',
        value: assetToEdit.value == null ? '' : String(assetToEdit.value),
        notes: assetToEdit.notes || '',
      });
      setStep(3);
    } else {
      resetForm();
    }
  };
  const closeModal = () => { setModalOpen(false); resetForm(); };

  const handleCategorySelect = e => {
    setForm(f => ({ ...f, typeCategory: e.target.value, type: '', institution: '' }));
    setStep(1);
  };
  const handleTypeSelect = e => {
    // After category + type selection, keep the user on a single "details" screen.
    // Auto-advancing on each keystroke is a poor UX on mobile.
    setForm(f => ({ ...f, type: e.target.value }));
    setStep(3);
  };
  const handleInstitution = e => {
    setForm(f => ({ ...f, institution: e.target.value }));
  };
  const handleAccountNo = e => setForm(f => ({ ...f, accountNo: e.target.value }));
  const handleValue = e => setForm(f => ({ ...f, value: e.target.value }));

  const fieldConfigForOther = (typeCategory) => {
    // We store data in a generic schema (institution/accountNo/notes). These labels
    // map that schema into user-friendly language per category.
    switch (typeCategory) {
      case 'Final Wishes':
        return {
          institutionLabel: 'Location / provider (optional)',
          institutionPlaceholder: 'e.g., burial site, crematorium, funeral director',
          institutionRequired: false,
          notesLabel: 'Instructions',
          notesPlaceholder: 'Write your burial instructions / wishes here…',
          notesRequired: true,
        };
      case 'Personal Notes':
        return {
          institutionLabel: 'Topic (optional)',
          institutionPlaceholder: 'e.g., For my children',
          institutionRequired: false,
          notesLabel: 'Note',
          notesPlaceholder: 'Write your note here…',
          notesRequired: true,
        };
      case 'Digital Assets':
        return {
          institutionLabel: 'Service / website',
          institutionPlaceholder: 'e.g., Gmail, Facebook, iCloud, Coinbase',
          institutionRequired: true,
          notesLabel: 'Notes (optional)',
          notesPlaceholder: 'Any instructions for access/recovery (optional)…',
          notesRequired: false,
        };
      case 'Physical Assets':
      default:
        return {
          institutionLabel: 'Location',
          institutionPlaceholder: 'e.g., home safe, safety deposit box, storage unit',
          institutionRequired: true,
          notesLabel: 'Notes (optional)',
          notesPlaceholder: 'Any extra details (optional)…',
          notesRequired: false,
        };
    }
  };

  const saveAsset = async e => {
    e.preventDefault();
    try {
      // Reference/accountNo is intentionally not shown in the UI for Other Assets.
      // To avoid accidentally wiping existing stored reference values, omit it from
      // payloads (both create and update).
      const payload = {
        type: form.type,
        typeCategory: form.typeCategory,
        institution: form.institution,
        notes: form.notes || '',
        ownerId: assetHolder.id,
      };
      if (editingAssetId) {
        await api.patch(`/assets/${encodeURIComponent(editingAssetId)}`, payload);
      } else {
        await api.post('/assets', payload);
      }
      await refreshAssets();
      closeModal();
    } catch (err) {
      const code = err?.response?.data?.code;
      if (err?.response?.status === 409 && code === 'NOMINEE_REQUIRED') {
        alert(getUserFacingErrorMessage(err, {
          fallback: 'Add at least one nominee before you can add assets.',
          includeSupportForUnknown: false,
        }));
        navigate('/account?tab=nominees', { state: { tab: 'nominees', returnTo: location.pathname } });
        return;
      }
      console.error('OtherAssets save failed:', err?.response?.data || err);
      alert(getUserFacingErrorMessage(err, {
        fallback: 'We could not save that item. Check your entries and try again.',
        includeSupportForUnknown: false,
      }));
    }
  };

  const deleteAsset = async (asset) => {
    if (!asset?.id) return;
    const ok = window.confirm('Delete this asset? This cannot be undone.');
    if (!ok) return;
    try {
      await api.delete(`/assets/${encodeURIComponent(asset.id)}`);
      await refreshAssets();
    } catch (err) {
      const code = err?.response?.data?.code;
      if (err?.response?.status === 409 && code === 'NOMINEE_REQUIRED') {
        alert(getUserFacingErrorMessage(err, {
          fallback: 'Add at least one nominee before you can change assets.',
          includeSupportForUnknown: false,
        }));
        navigate('/account?tab=nominees', { state: { tab: 'nominees', returnTo: location.pathname } });
        return;
      }
      alert(getUserFacingErrorMessage(err, {
        fallback: 'We could not delete that item. Please try again.',
        includeSupportForUnknown: false,
      }));
    }
  };

  return (
    <OwnerShell>
      <>
        <div style={{ padding: 20, position: 'relative' }}>
          {currentUser.type === 'owner' && hasNominees === false && (
            <NomineeRequiredBanner returnTo={location.pathname} />
          )}
          {currentUser.type === 'nominee' ? (
            <>
              {/* Owner selector dropdown */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
                <h1 style={{ margin: 0 }}>
                  {(() => {
                    const donor = beneficiaryOwners.find(o => o.ownerId === selectedOwnerId);
                    if (!donor) return 'Owner\'s Other Assets';
                    let profile = {};
                    try { profile = JSON.parse(localStorage.getItem('ownerProfile') || '{}'); } catch {}
                    const displayName = (profile.firstName && profile.lastName)
                      ? `${profile.firstName} ${profile.lastName}`
                      : (donor.ownerName || 'Owner');
                    return `${displayName}'s Other Assets`;
                  })()}
                </h1>
                <select
                  value={selectedOwnerId}
                  onChange={e => setSelectedOwnerId(e.target.value)}
                  style={{ padding: '8px 14px', borderRadius: 6, border: '1px solid #ccc', fontSize: 16, minWidth: 180 }}
                >
                  {beneficiaryOwners.map(o => (
                    <option key={o.ownerId} value={o.ownerId}>{(() => {
                      let profile = {};
                      try { profile = JSON.parse(localStorage.getItem('ownerProfile') || '{}'); } catch {}
                      return (profile.firstName && profile.lastName)
                        ? `${profile.firstName} ${profile.lastName}`
                        : (o.ownerName || o.ownerId);
                    })()}</option>
                  ))}
                </select>
              </div>
              {/* Category Tabs */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 24, marginTop: 8, flexWrap: 'wrap' }}>
                {OTHER_CATEGORIES.map(cat => (
                  <button
                    key={cat}
                    onClick={() => setActiveCategory(cat)}
                    style={{
                      padding: '8px 18px',
                      borderRadius: 12,
                      border: 'none',
                      background: activeCategory === cat ? 'var(--sage-600)' : 'var(--mist-200)',
                      color: activeCategory === cat ? '#fff' : 'var(--dusk-700)',
                      fontWeight: activeCategory === cat ? 600 : 400,
                      fontSize: 15,
                      cursor: 'pointer',
                      boxShadow: activeCategory === cat ? 'var(--shadow-soft)' : 'none',
                      transition: 'all 0.15s',
                    }}
                  >
                    {cat}
                  </button>
                ))}
              </div>
              {beneficiaryAssets.filter(a => a.typeCategory === activeCategory).length === 0 ? (
                <div style={{ marginTop: 60, textAlign: 'center', color: '#888' }}>
                  <p style={{ fontSize: 20 }}>No assets in this category yet.</p>
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 20, background: '#fff', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.07)' }}>
                  <thead>
                    <tr style={{ background: '#f5f5f5' }}>
                      <th style={{ padding: '12px 8px', borderBottom: '1px solid #eee', textAlign: 'left' }}>Type</th>
                      <th style={{ padding: '12px 8px', borderBottom: '1px solid #eee', textAlign: 'left' }}>Location/Details</th>
                      <th style={{ padding: '12px 8px', borderBottom: '1px solid #eee', textAlign: 'left' }}>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {beneficiaryAssets.filter(a => a.typeCategory === activeCategory).map(a => (
                      <tr key={a.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '10px 8px' }}>{a.type}</td>
                        <td style={{ padding: '10px 8px' }}>{a.institution}</td>
                        <td style={{ padding: '10px 8px' }}>{a.notes}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
                <h1 style={{ margin: 0 }}>Other Assets</h1>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  <ContextSwitcher />
                  <button
                    onClick={() => openModal()}
                    aria-disabled={hasNominees === false}
                    title={hasNominees === false ? 'Add a nominee first' : 'Add an asset'}
                    style={{
                      padding: '10px 18px',
                      fontSize: 16,
                      background: hasNominees === false ? '#8fa8a0' : 'var(--sage-600)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 12,
                      cursor: 'pointer',
                      boxShadow: 'var(--shadow-soft)',
                      fontWeight: 600,
                      opacity: hasNominees === false ? 0.85 : 1,
                      maxWidth: '100%',
                    }}
                  >
                    Add other asset
                  </button>
                </div>
              </div>
              {/* Category Tabs */}
              <ResponsiveOptionNav
                label="Category"
                options={OTHER_CATEGORIES}
                value={activeCategory}
                onChange={setActiveCategory}
              />
              {assets.filter(a => a.typeCategory === activeCategory).length === 0 ? (
                <div style={{ marginTop: 60, textAlign: 'center', color: '#888' }}>
                  <p style={{ fontSize: 20 }}>No assets in this category yet.</p>
                  <p>Click <b>Add other asset</b> to get started.</p>
                </div>
              ) : (
                <div style={{ overflowX: 'auto', borderRadius: 8 }}>
                  <table style={{ width: '100%', minWidth: 700, borderCollapse: 'collapse', marginTop: 20, background: '#fff', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.07)' }}>
                    <thead>
                      <tr style={{ background: '#f5f5f5' }}>
                        <th style={{ padding: '12px 8px', borderBottom: '1px solid #eee', textAlign: 'left' }}>Type</th>
                        <th style={{ padding: '12px 8px', borderBottom: '1px solid #eee', textAlign: 'left' }}>Location/Details</th>
                        <th style={{ padding: '12px 8px', borderBottom: '1px solid #eee', textAlign: 'left' }}>Notes</th>
                        <th style={{ padding: '12px 8px', borderBottom: '1px solid #eee', textAlign: 'left' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {assets.filter(a => a.typeCategory === activeCategory).map(a => (
                        <tr key={a.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                          <td style={{ padding: '10px 8px' }}>{a.type}</td>
                          <td style={{ padding: '10px 8px' }}>{a.institution}</td>
                          <td style={{ padding: '10px 8px' }}>{a.notes}</td>
                          <td style={{ padding: '10px 8px' }}>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                              <Button type="button" variant="primary" onClick={() => openModal(a)}>
                                Edit
                              </Button>
                              <Button type="button" variant="secondary" onClick={() => openAttachmentsModal(a)}>
                                Attachments
                              </Button>
                              <Button type="button" variant="danger" onClick={() => deleteAsset(a)}>
                                Delete
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
        {/* Modal for asset holder only */}
        {currentUser.type === 'owner' && (
          <Modal
            open={modalOpen}
            onClose={closeModal}
            title={editingAssetId ? 'Edit Other Asset' : 'Add Other Asset'}
            subtitle={editingAssetId ? 'Update the details of your asset.' : 'Enter the details of your asset below.'}
          >
            <form onSubmit={saveAsset} autoComplete="off" style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              {step === 0 && (
                <div style={{ marginBottom: 0 }}>
                  <label style={{ fontWeight: 500, color: '#333' }}>Asset Category<br />
                    <select name="typeCategory" value={form.typeCategory} onChange={handleCategorySelect} required style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #ccc', marginTop: 6 }}>
                      <option value="" disabled>Select category</option>
                      {OTHER_CATEGORIES.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  </label>
                </div>
              )}
              {step === 1 && (
                <div style={{ marginBottom: 0 }}>
                  <label style={{ fontWeight: 500, color: '#333' }}>Asset Type<br />
                    <select name="type" value={form.type} onChange={handleTypeSelect} required style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #ccc', marginTop: 6 }}>
                      <option value="" disabled>Select type</option>
                      {TYPE_OPTIONS[form.typeCategory]?.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  </label>
                  <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
                    <Button type="button" variant="secondary" onClick={() => setStep(0)}>
                      Back
                    </Button>
                  </div>
                </div>
              )}
              {step === 3 && (
                <>
                  {(() => {
                    const cfg = fieldConfigForOther(form.typeCategory);
                    return (
                      <>
                        <div style={{ marginBottom: 0 }}>
                          <label style={{ fontWeight: 500, color: '#333' }}>{cfg.institutionLabel}<br />
                            <input
                              name="other_institution"
                              autoComplete="off"
                              autoCorrect="off"
                              spellCheck={false}
                              placeholder={cfg.institutionPlaceholder}
                              value={form.institution}
                              onChange={handleInstitution}
                              required={cfg.institutionRequired}
                              style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #ccc', marginTop: 6 }}
                            />
                          </label>
                        </div>
                        <div style={{ marginBottom: 0 }}>
                          <label style={{ fontWeight: 500, color: '#333' }}>{cfg.notesLabel}{cfg.notesRequired ? '' : ' '}<br />
                            <textarea
                              name="other_notes"
                              autoComplete="off"
                              autoCorrect="off"
                              spellCheck={false}
                              placeholder={cfg.notesPlaceholder}
                              value={form.notes}
                              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                              required={cfg.notesRequired}
                              style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #ccc', marginTop: 6, minHeight: 110, resize: 'vertical' }}
                            />
                          </label>
                        </div>
                      </>
                    );
                  })()}
                  <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
                    <Button type="button" variant="secondary" onClick={() => setStep(1)}>
                      Back
                    </Button>
                    <Button type="submit" variant="primary" style={{ flex: 1 }}>
                      {editingAssetId ? 'Save changes' : 'Save'}
                    </Button>
                  </div>
                </>
              )}
            </form>
          </Modal>
        )}

        <Modal
          open={attachmentsOpen}
          onClose={() => {
            setAttachmentsOpen(false);
            setAttachmentsAsset(null);
            setAttachments([]);
            setAttachmentsError('');
            setAttachmentFile(null);
            setAttachmentUploadProgress(null);
            setAttachmentUploadBusy(false);
          }}
          title="Attachments"
          subtitle={attachmentsAsset ? `${attachmentsAsset.typeCategory}: ${attachmentsAsset.type}` : 'Upload and download files'}
        >
          <div style={{ display: 'grid', gap: 12 }}>
            {attachmentsError && (
              <div style={{ padding: 10, borderRadius: 10, background: 'var(--peach-50)', border: '1px solid var(--peach-300)', color: 'var(--peach-700)' }}>
                {attachmentsError}
              </div>
            )}

            <div style={{ display: 'grid', gap: 8 }}>
              <div style={{ fontWeight: 700 }}>Upload attachment</div>
              <div style={{ fontSize: 13, color: 'var(--dusk-700)' }}>
                Supported: PDF/DOCX/JPG/PNG, audio up to 250MB, video up to 5GB (multipart).
              </div>
              <input
                type="file"
                onChange={(e) => setAttachmentFile(e.target.files?.[0] || null)}
                disabled={attachmentUploadBusy}
              />
              {attachmentUploadProgress && (
                <div style={{ fontSize: 13, color: 'var(--dusk-700)' }}>
                  {attachmentUploadProgress.label} {Number.isFinite(attachmentUploadProgress.pct) ? `(${attachmentUploadProgress.pct}%)` : ''}
                </div>
              )}
              <Button
                type="button"
                variant="primary"
                onClick={uploadAttachment}
                disabled={!attachmentFile || attachmentUploadBusy || !attachmentsAsset}
              >
                {attachmentUploadBusy ? 'Uploading…' : 'Upload'}
              </Button>
            </div>

            <div style={{ display: 'grid', gap: 10 }}>
              <div style={{ fontWeight: 700 }}>Existing attachments</div>
              {attachmentsBusy ? (
                <div style={{ color: 'var(--dusk-700)' }}>Loading…</div>
              ) : attachments.length === 0 ? (
                <div style={{ color: 'var(--dusk-700)' }}>No attachments yet.</div>
              ) : (
                <div style={{ display: 'grid', gap: 8 }}>
                  {attachments.map((a) => (
                    <div key={a.objectId} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, border: '1px solid var(--mist-300)', borderRadius: 10, padding: 10 }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.fileName || 'attachment'}</div>
                        <div style={{ fontSize: 12, color: 'var(--dusk-700)' }}>
                          {(a.contentType || 'unknown type')}{a.bytes ? ` • ${a.bytes} bytes` : ''}
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={async () => {
                          try {
                            await downloadAttachment({ assetId: attachmentsAsset.id, objectId: a.objectId });
                          } catch (e) {
                            console.error(e);
                            setAttachmentsError('Failed to download attachment.');
                          }
                        }}
                      >
                        Download
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </Modal>
      </>
    </OwnerShell>
  );
} 